import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { getContentByType, getContentFromCache } from '../api/contentApi';

const PRACTICE_AREAS_I18N: Record<string, { title: string; items: string[] }> = {
  de: {
    title: 'Tätigkeitsschwerpunkte',
    items: [
      'Bank- und Kapitalmarktrecht',
      'Arbeitsrecht',
      'Mietrecht',
      'Allgemeines Zivilrecht',
      'Zivilprozessrecht',
    ],
  },
  fr: {
    title: 'Domaines de compétence',
    items: [
      'Droit bancaire et des marchés financiers',
      'Droit du travail',
      'Droit du bail',
      'Droit civil général',
      'Droit de la procédure civile',
    ],
  },
  en: {
    title: 'Practice Areas',
    items: [
      'Banking & Capital Markets Law',
      'Employment Law',
      'Tenancy Law',
      'General Civil Law',
      'Civil Procedure Law',
    ],
  },
};

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'de';
  const areas = PRACTICE_AREAS_I18N[currentLang] || PRACTICE_AREAS_I18N.de;

  const [portraitImageUrl, setPortraitImageUrl] = useState<string | null>(() => {
    const cached = getContentFromCache('portrait');
    return cached?.[0]?.portraitImageUrl
      ?? localStorage.getItem('portrait_image_url')
      ?? process.env.REACT_APP_PORTRAIT_IMAGE_URL
      ?? null;
  });

  useEffect(() => {
    getContentByType('portrait').then((items) => {
      if (items.length > 0 && items[0].portraitImageUrl) {
        const url = items[0].portraitImageUrl;
        setPortraitImageUrl(url);
        localStorage.setItem('portrait_image_url', url);
      }
    }).catch(() => { /* non-critical, hero renders without portrait */ });
  }, []);

  return (
    <>
      <Helmet>
        <title>CLAIRMONT | Rechtsanwalt Marcel Küster</title>
        <html lang={currentLang} />
        {portraitImageUrl && (
          <link rel="preload" as="image" href={portraitImageUrl} />
        )}
      </Helmet>

      {/* Hero Section — full viewport height, existing avif image */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="flex flex-col items-center justify-center gap-6 mb-4">
            {portraitImageUrl && (
              <img
                src={portraitImageUrl}
                alt="Rechtsanwalt Marcel Küster"
                className="hero-portrait"
                fetchPriority="high"
                loading="eager"
              />
            )}
            <h1>Rechtsanwalt Marcel Küster</h1>
          </div>
          <p className="mb-10">{t('home.subtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={`/${currentLang}/contact`}
              className="bg-primary-500 hover:bg-primary-600 text-white font-semibold px-8 py-3 rounded-lg transition text-lg"
            >
              {t('home.inquiryButton')}
            </Link>
            <Link
              to={`/${currentLang}/appointment`}
              className="border-2 border-white/30 hover:border-white/60 text-white font-semibold px-8 py-3 rounded-lg transition text-lg"
            >
              {currentLang === 'de' ? 'Termin buchen' : currentLang === 'fr' ? 'Prendre rendez-vous' : 'Book an appointment'}
            </Link>
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-4xl font-serif font-bold text-primary-800 text-center mb-12">
            {areas.title}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {areas.items.map((area, i) => (
              <div
                key={i}
                className="bg-gray-50 border border-gray-100 rounded-lg p-6 text-center hover:shadow-md transition"
              >
                <span className="text-primary-500 text-2xl mb-3 block">&#9670;</span>
                <h3 className="text-lg font-serif font-semibold text-primary-800">{area}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About teaser */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl font-serif font-bold text-primary-800 mb-6">
            {currentLang === 'de' ? 'Über den Anwalt' : currentLang === 'fr' ? 'À propos de l\'avocat' : 'About the Lawyer'}
          </h2>
          <p className="text-gray-600 text-lg font-serif mb-8 max-w-2xl mx-auto">
            {currentLang === 'de'
              ? 'Erfahren Sie mehr über Rechtsanwalt Marcel Küster, seine Qualifikationen und Erfahrungen.'
              : currentLang === 'fr'
              ? 'En savoir plus sur l\'avocat Marcel Küster, ses qualifications et son expérience.'
              : 'Learn more about lawyer Marcel Küster, his qualifications, and experience.'}
          </p>
          <Link
            to={`/${currentLang}/portrait`}
            className="inline-block bg-primary-500 hover:bg-primary-600 text-white font-semibold px-8 py-3 rounded-lg transition"
          >
            {currentLang === 'de' ? 'Zum Portrait' : currentLang === 'fr' ? 'Voir le portrait' : 'View Portrait'}
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl font-serif font-bold mb-4">
            {currentLang === 'de' ? 'Kontaktieren Sie mich' : currentLang === 'fr' ? 'Contactez-moi' : 'Contact Me'}
          </h2>
          <p className="text-gray-300 text-lg font-serif mb-8">
            {currentLang === 'de'
              ? 'Sie erreichen mich über das Kontaktformular, per E-Mail oder per Telefon.'
              : currentLang === 'fr'
              ? 'Vous pouvez me contacter via le formulaire de contact, par e-mail ou par téléphone.'
              : 'You can reach me via the contact form, by email, or by phone.'}
          </p>
          <Link
            to={`/${currentLang}/contact`}
            className="inline-block bg-primary-500 hover:bg-primary-600 text-white font-semibold px-8 py-3 rounded-lg transition text-lg"
          >
            {t('contact.submit')}
          </Link>
        </div>
      </section>
    </>
  );
};

export default HomePage;
